/**
 * @file docVersionService.ts
 * @description
 * This service provides functionality to manage document versioning for submissions.
 * Specifically, it includes a function to mark older documents as inactive for a given submission.
 *
 * @dependencies
 * - Supabase JS client: Used to run queries on the `documents` table.
 * - Logging helpers: Provides consistent log messages for debugging and error tracking.
 *
 * @notes
 * - We rely on the assumption that the `documents` table includes an `is_active` column
 *   that can be set to false for older documents.
 * - We assume that the list of new doc IDs is accurate and that all newly created or
 *   relevant documents for the final submission pass these IDs to stay active.
 * - This service only performs an UPDATE query. It does not handle creation or deletion
 *   of document records, which is done elsewhere (e.g., docStorageService.ts).
 */

import { getSupabaseClient } from '../lib/supabaseClient';

/**
 * We create a standalone Supabase client with the same credentials that would be used
 * in other services. Typically, you might import a shared client from `lib/supabaseClient.ts`
 * or similar. For consistency with the project's pattern, we replicate the approach
 * done in other files. Adjust if needed to reuse a single client instance.
 */
const supabase = getSupabaseClient();

/**
 * Helper function for structured logging. This ensures consistent log messages
 * with an 'operation' label, 'timestamp', and any relevant details or errors.
 *
 * @param operation - A short string describing the operation performed
 * @param details - An object with relevant key-value pairs about the operation
 * @param error - An optional error object or string
 * @returns The log object that was output
 */
function logVersionServiceOperation(
  operation: string,
  details: Record<string, any>,
  error?: any
) {
  const timestamp = new Date().toISOString();
  const logObj = {
    operation,
    timestamp,
    details,
    ...(error && {
      error:
        typeof error === 'object'
          ? {
              message: error.message,
              name: error.name,
              stack: error.stack,
            }
          : error,
    }),
  };

  if (error) {
    console.error(`DOC_VERSION_SERVICE [${operation}]:`, logObj);
  } else {
    console.log(`DOC_VERSION_SERVICE [${operation}]:`, logObj);
  }
  return logObj;
}

/**
 * markOlderDocsInactive
 * ---------------------
 * Sets is_active = false on any documents belonging to the specified submission
 * that are not in the provided list of doc IDs to remain active.
 *
 * @param submissionId - The UUID of the submission in the `submissions` table
 * @param newDocIds - The array of doc IDs that should remain active
 * @throws An error if the Supabase update fails
 *
 * @description
 * This function is used to manage document versions by marking older documents
 * inactive for the same submission. For example, if we finalize a new version
 * of the PDF/Markdown or the user re-uploads certain files, we want to ensure
 * that only the newly created or currently relevant documents stay active.
 *
 * @example
 * ```
 * await markOlderDocsInactive('123e4567-e89b-12d3-a456-426614174000', [
 *   '234e4567-e89b-12d3-a456-426614174000',
 *   '345e4567-e89b-12d3-a456-426614174000'
 * ]);
 * // This will set is_active=false for all documents for the submission except
 * // the ones specified in the newDocIds array.
 * ```
 */
export async function markOlderDocsInactive(
  submissionId: string,
  newDocIds: string[]
): Promise<void> {
  logVersionServiceOperation('markOlderDocsInactive:start', {
    submissionId,
    newDocIdsCount: newDocIds.length,
  });

  if (!submissionId) {
    const errMsg = 'Submission ID is required';
    logVersionServiceOperation('markOlderDocsInactive:error', { errMsg }, errMsg);
    throw new Error(errMsg);
  }

  // If newDocIds is empty, the logic still sets all docs to inactive.
  // That scenario might be unusual, but it's allowed.
  try {
    // Perform the update
    const { data, error } = await supabase
      .from('documents')
      .update({ 
        is_active: false,
        updated_at: new Date().toISOString()
      })
      .match({ submission_id: submissionId })
      .not('id', 'in', `(${newDocIds.map((id) => `${id}`).join(',')})`)
      .select('id');

    if (error) {
      logVersionServiceOperation(
        'markOlderDocsInactive:update-failed',
        { submissionId },
        error
      );
      throw new Error(`Failed to mark older docs inactive: ${error.message}`);
    }

    logVersionServiceOperation('markOlderDocsInactive:success', {
      submissionId,
      updatedCount: data?.length || 0,
    });
  } catch (err) {
    logVersionServiceOperation('markOlderDocsInactive:unexpected-error', { submissionId }, err);
    throw err;
  }
}
